import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy
} from '@angular/core';
import { combineLatest, zip, of, Subscription, Observable } from 'rxjs';
import { take, switchMap, map, tap, distinctUntilChanged, filter } from 'rxjs/operators';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import isEmpty from 'lodash-es/isEmpty';

import { OnAttach, OnDetach } from '@app/modules/core/directives/app-router-outlet';
import { DeeplinkPlacePipe } from '@app/pipes/deeplink-place.pipe';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { mapExchangePayload } from '@flight-results/mappers/exchange-payload.mapper';
import { ExchangeSearchParamsSectors, SearchTraveler } from '@user-history/entities/user-history-item';
import { PassengerType, PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { SearchType } from '@search-bar/entities/search-type';
import { PaxType } from '@booking/entities/pax-type';
import { FlightType } from '@search-bar/entities/flight-type';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { disabledDepartureDate, getStringDateByUrl } from '@user-history/mappers/exchange-disabled-sector.mapper';
import { getDisabledDate, getReturnPointDate, isOnlyArrivalSector } from '@search-bar/mappers/exchange-url.mapper';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { isValidFormatJSON } from '@app/modules/core/mappers/is-json.mapper';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { stringToBoolean } from '@app/modules/core/mappers/string-to-boolean.mapper';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { RequestSearchPayload } from '@flight-results/entities/request-search.payload';

@Component({
  selector: 'app-flight-results-exchanges',
  templateUrl: './flight-results-exchanges.container.html',
  styleUrls: ['./flight-results-exchanges.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FlightResultsExchangesContainer implements OnAttach, OnDetach, OnInit, OnDestroy {

  public disableScroll: boolean;
  public exchangeData: ExchangeSearchParamsSectors;
  public params = {} as RequestSearchPayload;

  private searchType: SearchType = SearchType.FLIGHT_RESULTS;
  private subscriptions: Subscription[] = [];

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private placePipe: DeeplinkPlacePipe,
    private changeDetectorRef: ChangeDetectorRef,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private mobileDeviceService: MobileDeviceService,
    private domManipulateService: DomManipulateService,
    private readonly facade: FlightResultsFacade,
    private readonly avoService: AvoService
  ) { }

  public onAttach(activatedRoute: ActivatedRoute): void {
    this.disableScroll = false;
  }

  public onDetach(): void {
    this.disableScroll = true;
    this.changeDetectorRef.detectChanges();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.isMobile$.subscribe(isMobile => {
        if (!isMobile) this.router.navigate(['/']);
        else this.domManipulateService.setBodyScroll(false);
      })
    );
    this.listenParams();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get isLoadingExchange$(): Observable<boolean> {
    return this.facade.isLoadingExchange$;
  }

  public onDeactivate(): void {
    if (this.params && !isEmpty(this.params)) {
      this.facade.errorInExchangeBook$.pipe(take(1)).subscribe(withError => {
        if (withError) this.facade.exchangeSearch({ ...this.params } as RequestSearchPayload);
      }).unsubscribe();
    }
  }

  private listenParams(): void {
    this.subscriptions.push(
      combineLatest([
        this.route.paramMap,
        this.route.queryParamMap.pipe(take(1)),
        this.isMobile$.pipe(distinctUntilChanged())
      ]).pipe(
        switchMap(([params, queries, isMobile]) => zip(
          of({ params, queries, isMobile }),
          this.placePipe.transform(params.get('arrival')).pipe(filter((place) => !isEmpty(place))),
          this.placePipe.transform(params.get('departure')).pipe(filter((place) => !isEmpty(place))),
        )),
        map(([{ params, queries, isMobile }, arrival, departure]) => ({ params, queries, arrival, departure, isMobile })),
        tap(({ params, queries, arrival, departure, isMobile }) => {
          const branchCode = queries.get('branch_code');
          const salesChannel = queries.get('sales_channel');
          const departurePlace = {
            code: departure.cityCode, country: departure.country, description: departure.name
          } as SelectAutocompletePlace;
          const arrivalPlace = {
            code: arrival.cityCode, country: arrival.country, description: arrival.name
          } as SelectAutocompletePlace;
          this.searchByParams(params, isMobile, queries);
          this.setPassengerArrangement({
            id: PassengerType.ADULTS, quantity: this.params.Adults ? parseInt(this.params.Adults) : 1
          });
          this.setPassengerArrangement({
            id: PassengerType.CHILDREN, quantity: this.params.Children ? parseInt(this.params.Children) : 0
          });
          this.setPassengerArrangement({
            id: PassengerType.INFANTS, quantity: this.params.Infants ? parseInt(this.params.Infants) : 0
          });
          this.baseSearchBarFacade.selectPlace({ ...arrivalPlace, type: PlaceType.ARRIVAL }, false, this.searchType);
          this.baseSearchBarFacade.selectPlace({ ...departurePlace, type: PlaceType.DEPARTURE }, false, this.searchType);
          if (branchCode) this.baseSearchBarFacade.setBranchCode(branchCode);
          if (salesChannel) this.baseSearchBarFacade.setSalesChannel(salesChannel);
        }),
        switchMap(({ params, queries, arrival, departure }) => zip(
          of({ params, queries, arrival, departure }),
          this.baseSearchBarFacade.searchData$
        )),
        map(data => ({ ...data[0], searchData: data[1] })),
        tap(({ searchData }) => {
          this.params.RequestSettings.BranchCode = searchData.branchCode;
          this.params.RequestSettings.SalesChanelCode = searchData.salesChannel;
        })
      ).subscribe(({ searchData, params }) => {
        const payload = { ...searchData };
        this.exchangeData = {
          Trips: this.params.Trips,
          TravelItineraryId: this.params.TravelItineraryId,
          AirReservationId: this.params.AirReservationId,
          Passengers: this.createPassengerArray(),
          exchangeDisabledSector: this.params.exchangeDisabledSector
        };
        const isOnlyArrival = isOnlyArrivalSector(this.params.Trips);
        if (this.params.Trips.length === 1 && params.get('departure_date')) {
          if (isOnlyArrival) {
            payload.arrivalDate = getReturnPointDate(this.exchangeData);
            payload.departureDate = getDisabledDate(this.exchangeData);
          } else payload.departureDate = new Date(params.get('departure_date').replace(new RegExp(/-/g), '/'));
          payload.flightType = FlightType.ONEWAY;
        } else if ((this.params.Trips.length === 2 && params.get('departure_date') && params.get('arrival_date'))) {
          payload.departureDate = new Date(params.get('departure_date').replace(new RegExp(/-/g), '/'));
          payload.arrivalDate = new Date(params.get('arrival_date').replace(new RegExp(/-/g), '/'));
          payload.flightType = FlightType.ROUNDTRIP;
        }
        payload.baggageIncluded = params.get('baggage_included').toLowerCase() === 'true';
        this.baseSearchBarFacade.saveExchangeSearchData(payload);
        const isOneWay = payload.flightType === FlightType.ONEWAY;
        const paramValues = {
          originalArrival: isOnlyArrival ? params.get('original_departure_date') : params.get('original_arrival_date'),
          originalDeparture: isOnlyArrival ? null : params.get('original_departure_date'),
          originalRoute: params.get('original_route'),
          originalDepartureDateTime: isOnlyArrival ? null : params.get('original_departure_date_time'),
          originalArrivalDateTime: isOnlyArrival ? params.get('original_departure_date_time') :
            params.get('original_arrival_date_time'),
          exchangeWithAssistanceAndLongerDuration: stringToBoolean(params.get('with_assistance_and_longer_duration')),
          exchangeWithAssistanceAndShorterOrEqualDuration: stringToBoolean(
            params.get('with_assistance_and_shorter_or_equal_duration'))
        };
        const exchangeDataWithParams = { ...this.exchangeData, ...paramValues };
        const exchangeDataTrips = this.exchangeData.Trips.map(trip => ({
          ...trip, DepartureDate: getStringDateByUrl(trip, exchangeDataWithParams)
        }));
        const exchangeDataWithParamsAndFullTrips = { ...exchangeDataWithParams, Trips: exchangeDataTrips };
        const departureSelectionDisabled = disabledDepartureDate(exchangeDataWithParamsAndFullTrips, isOnlyArrival, isOneWay);
        this.exchangeData = {
          ...exchangeDataWithParamsAndFullTrips,
          departureSelectionDisabled: departureSelectionDisabled,
          originalDepartureDateTime: params?.get('original_departure_date_time'),
          originalDeparture: params?.get('original_departure_date'),
          RouteChangeIsDisabled: params.get('route_change_is_disabled').toLowerCase() === 'true',
        }
        this.facade.exchangeSearch({ ...this.params } as RequestSearchPayload);
        this.registerEventExchangesResults();
      })
    );
  }

  private registerEventExchangesResults(): void {
    this.avoService.getEventTriggerProps$().pipe(
      tap(trigger => {
        this.AvoDispatch.viewTravelChangesResults({
          ...this.avoService.avoBaseProperties(trigger)
        })
      }
      )).subscribe().unsubscribe();
  }

  private searchByParams(params: ParamMap, isMobile: boolean, queries: ParamMap,): void {
    const disabledSector = queries.get('sector_disabled');
    const _params = {
      ...params['params'],
      departure_type: queries.get('departure_type'),
      arrival_type: queries.get('arrival_type')
    }
    if (disabledSector && isValidFormatJSON(disabledSector)) {
      this.params = mapExchangePayload(_params, isMobile, JSON.parse(disabledSector));
    } else{
      this.params = mapExchangePayload(_params, isMobile, []);
    }
  }

  private setPassengerArrangement(event: PassengerArrangement): void {
    this.baseSearchBarFacade.setPassengerArrangement(event);
  }
  private createPassengerArray(): SearchTraveler[] {
    const adults = [].concat(new Array(Number(this.params.Adults)).fill(null)).map(() => ({ PaxType: PaxType.ADULTS }));
    const children = [].concat(new Array(Number(this.params.Children)).fill(null)).map(() => ({ PaxType: PaxType.CHILDREN }));
    const infants = [].concat(new Array(Number(this.params.Infants)).fill(null)).map(() => ({ PaxType: PaxType.INFANTS }));
    return adults.concat(children).concat(infants);
  }

}
